import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { activeSession } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { spinner, errorState, emptyState, toast, confirmDialog, openModal } from "../components/ui.js";
import { openExportDoc } from "../components/exportModal.js";
import { programToMarkdown } from "../lib/exportDoc.js";
import { programCoverage } from "../components/volumeCoverage.js";
import { planTabs } from "../components/planTabs.js";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Local 'YYYY-MM-DD' for (today − n days). Used to anchor a program so today maps to slot n.
function isoDateMinusDays(n) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Programs() {
  const root = h("div", { class: "screen" });
  load();

  async function load() {
    setChildren(root, header(), planTabs("programs"), spinner());
    try {
      const programs = await api.programs();
      render(programs);
    } catch (e) {
      setChildren(root, header(), planTabs("programs"), errorState(e.message, load));
    }
  }

  function header() {
    return topbar("Plan", {
      sub: "Templates and programs",
      actions: [
        h("button", { class: "icon-btn", onClick: () => navigate("/import?mode=doc"), "aria-label": "Import a program document" }, icon("download", { size: 20 })),
        h("button", { class: "icon-btn", onClick: () => navigate("/programs/new"), "aria-label": "New program" }, icon("plus", { size: 22 })),
      ],
    });
  }

  function render(programs) {
    setChildren(root,
      header(),
      planTabs("programs"),
      programs.length
        ? h("div", { class: "mt" }, ...programs.map(card))
        : emptyState({
            icon: "list",
            title: "No programs yet",
            message: "Build a weekly schedule of your workouts — the dashboard then tells you what to train each day.",
            action: h("div", null,
              h("button", { class: "btn btn-primary", onClick: () => navigate("/programs/new") }, "Create a program"),
              h("button", { class: "btn btn-ghost btn-block mt", onClick: () => navigate("/import?mode=doc") }, "Import from a document")
            ),
          })
    );
  }

  function card(p) {
    const workoutDays = p.days.filter((d) => d.workout).length;
    return h(
      "div",
      { class: "card", style: { marginBottom: "12px" } },
      h("div", { class: "row-between", style: { alignItems: "flex-start" } },
        h("div", null,
          h("div", { style: { fontWeight: 700, fontSize: "1.1rem" } }, p.name,
            p.is_active ? h("span", { class: "badge", style: { marginLeft: "8px", background: "var(--success)", color: "#fff", verticalAlign: "middle" } }, "Active") : null,
            p.is_public ? h("span", { class: "badge", style: { marginLeft: "8px", verticalAlign: "middle" } }, "Public") : null),
          h("div", { class: "dim", style: { marginTop: "2px", fontSize: ".84rem" } },
            `${p.days.length} day${p.days.length === 1 ? "" : "s"} · ${workoutDays} workout${workoutDays === 1 ? "" : "s"}`,
            p.is_public && p.use_count ? ` · ${p.use_count} enrolled` : ""),
          h("div", { class: "dim", style: { marginTop: "2px", fontSize: ".8rem" } },
            `${repeatLabel(p)}${p.is_active && p.anchored ? " · follows the calendar" : ""}`),
          p.description ? h("div", { class: "dim", style: { marginTop: "4px", fontSize: ".82rem" } }, p.description) : null
        ),
        h("button", { class: "btn btn-ghost", "aria-label": `Options for ${p.name}`, onClick: () => menu(p) }, icon("grip", { size: 18 }))
      ),
      p.today ? todayBlock(p) : h("p", { class: "dim mt" }, "No days yet — tap edit to build the schedule."),
      h("div", { class: "btn-row mt" },
        h("button", { class: "btn btn-ghost grow", onClick: () => navigate(`/programs/${p.id}`) }, icon("edit", { size: 18 }), "Edit"),
        p.is_active
          ? h("button", { class: "btn btn-ghost grow", onClick: () => startProgram(p) }, icon("history", { size: 18 }), "Set start day")
          : h("button", { class: "btn btn-primary grow", onClick: () => startProgram(p) }, icon("play", { size: 18, fill: true }), "Start")
      )
    );
  }

  function repeatLabel(p) {
    return p.days.length === 7 ? "Repeats weekly" : `Repeats every ${p.days.length} day${p.days.length === 1 ? "" : "s"}`;
  }

  // The current day in the schedule, with a Start button (workout) or advance (rest).
  function todayBlock(p) {
    const d = p.today;
    const upNext = p.next && (!d.workout || p.next.id !== d.id)
      ? h("div", { class: "dim", style: { fontSize: ".8rem", marginTop: "6px" } }, `Up next: ${p.next.workout.name}`)
      : null;

    if (!d.workout) {
      // Calendar-anchored programs advance their rest days automatically; manual ones need a nudge.
      const restAction = p.anchored
        ? h("span", { class: "dim", style: { fontSize: ".8rem" } }, "Rest day")
        : h("button", { class: "btn btn-ghost", onClick: () => advance(p) }, "Mark done", icon("forward", { size: 16 }));
      return h("div", { class: "card", style: { background: "var(--bg)", marginTop: "10px" } },
        h("div", { class: "row-between", style: { alignItems: "center" } },
          h("div", null, h("div", { style: { fontWeight: 600 } }, d.label || "Rest day"), h("div", { class: "dim", style: { fontSize: ".8rem" } }, "Recovery — no workout today")),
          restAction
        ),
        upNext
      );
    }

    const primary = (d.workout.exercises || []).flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "84px";
    const action = p.anchored && p.done_today
      ? h("span", { class: "hstack", style: { gap: "6px", color: "var(--success)", fontWeight: 700, fontSize: ".88rem" } }, icon("check", { size: 18 }), "Done today")
      : h("button", { class: "btn btn-primary", onClick: () => begin(d.workout.id, p.id) }, icon("play", { size: 18, fill: true }), "Start");
    return h("div", { class: "card hstack", style: { background: "var(--bg)", marginTop: "10px", alignItems: "center" } },
      mini,
      h("div", { class: "grow" },
        h("div", { class: "dim", style: { fontSize: ".72rem", fontWeight: 700, letterSpacing: ".04em" } }, "TODAY"),
        h("div", { style: { fontWeight: 700 } }, d.label || d.workout.name),
        h("div", { class: "dim", style: { fontSize: ".8rem" } }, `${d.workout.exercises.length} exercise${d.workout.exercises.length === 1 ? "" : "s"}`),
        upNext
      ),
      action
    );
  }

  async function begin(workoutId, programId) {
    if (activeSession()) {
      const ok = await confirmDialog({ title: "Resume or replace?", message: "You already have an active session. Open it instead?", confirmText: "Open active" });
      if (ok) navigate("/session");
      return;
    }
    try {
      const session = await api.startSession({ workout_id: workoutId, program_id: programId });
      activeSession(session);
      navigate("/session");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function advance(p) {
    try {
      await api.advanceProgram(p.id);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  // Activate a program, choosing which day you're on today and whether it tracks the calendar.
  function startProgram(p) {
    if (!p.days.length) { toast("Add days to this program first", "warn"); return; }
    const weekly = p.days.length === 7;
    let anchor = true; // calendar tracking on by default — what most people want for a weekly split
    // Default selection: for a weekly schedule, today's weekday; otherwise wherever the cursor is.
    const todayDow = (new Date().getDay() + 6) % 7; // Mon=0 … Sun=6
    let sel = weekly ? todayDow : (p.today_index || 0);

    const dayList = h("div");
    function renderDays() {
      setChildren(dayList, ...p.days.map((d, i) =>
        h("button", { class: ["prog-day-opt", i === sel && "sel"], onClick: () => { sel = i; renderDays(); } },
          h("span", { class: "pdo-idx" }, weekly ? WEEKDAYS[i] : `Day ${i + 1}`),
          h("span", { class: "pdo-name grow" }, d.workout ? d.workout.name : (d.label || "Rest day")),
          i === sel ? icon("check", { size: 16 }) : null
        )
      ));
    }
    renderDays();

    const knob = h("span", { class: "switch-knob" });
    const sw = h("button", { class: ["switch", anchor && "on"], "aria-label": "Track by calendar", onClick: () => { anchor = !anchor; sw.classList.toggle("on", anchor); } }, knob);
    const anchorRow = h("div", { class: "row-between", style: { marginTop: "14px" } },
      h("div", { style: { paddingRight: "10px" } },
        h("div", { style: { fontWeight: 600 } }, "Track by calendar"),
        h("div", { class: "dim", style: { fontSize: ".8rem", marginTop: "2px" } },
          weekly ? "Each weekday shows its workout automatically; repeats weekly." : `Today's day is set by the date; repeats every ${p.days.length} days.`)
      ),
      sw
    );

    const m = openModal({
      title: `Start "${p.name}"`,
      content: h("div", null,
        h("p", { class: "dim", style: { fontSize: ".85rem", marginBottom: "10px" } },
          weekly ? "Pick the day you're on today — it'll then follow the days of the week on its own." : "Pick the day you're starting on."),
        dayList,
        anchorRow,
        h("button", { class: "btn btn-primary btn-block btn-lg mt-lg", onClick: confirm }, icon("play", { size: 20, fill: true }), p.is_active ? "Update schedule" : "Start program")
      ),
    });

    async function confirm() {
      m.close();
      const payload = { is_active: true };
      if (anchor) payload.start_date = isoDateMinusDays(sel);
      else { payload.start_date = null; payload.cursor = sel; }
      try {
        await api.updateProgram(p.id, payload);
        toast(p.is_active ? "Schedule updated" : `"${p.name}" started`, "success");
        load();
      } catch (e) {
        toast(e.message, "error");
      }
    }
  }

  function menu(p) {
    const m = openModal({
      title: p.name,
      content: h("div", null,
        h("button", { class: "btn btn-ghost btn-block", onClick: () => { m.close(); analyze(p); } }, icon("chart", { size: 18 }), "Muscle coverage"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); navigate(`/programs/${p.id}`); } }, icon("edit", { size: 18 }), "Edit schedule"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); startProgram(p); } }, icon("history", { size: 18 }), p.is_active ? "Set start day" : "Start / set start day"),
        p.is_public
          ? h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); unpublish(p); } }, icon("x", { size: 18 }), "Make private")
          : h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); publish(p); } }, icon("forward", { size: 18 }), "Publish to Discover"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); exportDoc(p); } }, icon("download", { size: 18 }), "Export document"),
        h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: () => { m.close(); del(p); } }, icon("trash", { size: 18 }), "Delete program")
      ),
    });
  }

  // Projected weekly hard sets per muscle if every workout is done as planned. The list already
  // carries each day's full workout exercises (withSchedule → workoutSummary), so this computes
  // client-side with no extra fetch — see lib/programVolume.js.
  function analyze(p) {
    const days = (p.days || []).map((d) => ({ exercises: (d.workout && d.workout.exercises) || [] }));
    openModal({
      title: `Muscle coverage · ${p.name}`,
      content: programCoverage(days, { cycleDays: days.length }),
    });
  }

  // The program list carries only light per-day workout summaries (id + name), so fetch the
  // full workouts (with exercises) and map day → full workout before building the document.
  async function exportDoc(p) {
    try {
      const all = await api.workouts();
      const byId = new Map(all.map((w) => [w.id, w]));
      const exerciseIds = (p.days || [])
        .flatMap((d) => (d.workout ? (byId.get(d.workout.id)?.exercises || []) : []))
        .map((e) => e.library_exercise_id);
      openExportDoc({
        title: p.name,
        exerciseIds,
        build: ({ perf }) => programToMarkdown(p, byId, { perf }),
      });
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function publish(p) {
    let description = p.description || "";
    const textarea = h("textarea", { class: "input", rows: 3, placeholder: "Describe your program (optional)", oninput: (e) => (description = e.target.value) }, description);
    const m = openModal({
      title: `Publish "${p.name}"`,
      content: h("div", null,
        h("p", { class: "dim" }, "Anyone can find this in Discover, see you as the author, and enroll — which copies the program and its workouts into their account."),
        h("div", { class: "field mt" }, h("label", null, "Description"), textarea),
        h("button", { class: "btn btn-primary btn-block mt", onClick: async () => {
          m.close();
          try { await api.publishProgram(p.id, { description }); toast("Published to Discover", "success"); load(); }
          catch (e) { toast(e.message, "error"); }
        } }, icon("forward", { size: 18 }), "Publish")
      ),
    });
  }

  async function unpublish(p) {
    try { await api.unpublishProgram(p.id); toast("Made private", "success"); load(); }
    catch (e) { toast(e.message, "error"); }
  }

  async function del(p) {
    const ok = await confirmDialog({ title: "Delete program?", message: `"${p.name}" will be removed. Your workouts and history are kept.`, confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteProgram(p.id);
      toast("Program deleted", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  return root;
}
